import { z } from "zod";

import { loadIdentityAnswer } from "./clarity-identity";
import { readAnalyticsIdentity } from "./identity-cookies-client";
import type { IdentityCookies } from "./identity-cookies";

/**
 * The `user` block of the `dataLayer` (INST §10): who is behind the browser, in the terms the
 * analytics ask — and nothing personal.
 *
 * ```
 * user_id              the backend's keyed hash of the customer id; absent until the backend serves it
 * anonymous_id         the first-party cookie's UUID; only after the accept (D-06)
 * first_touch_list_id  the id of the first list visited, written once; only after the accept
 * logged_in            whether there is a session
 * viewer_role          internal | host | guest
 * ```
 *
 * `first_touch_list_id` is a user-scoped property in the INST (§10), so it travels with the identity
 * and not with the list: the `list` block says which list the *event* belongs to.
 *
 * Never here: CPF, e-mail, name, the slug of a list (D-02). The identity route's answer carries the
 * e-mail for Clarity's sake; this module reads the *kind* of visitor out of it and drops the rest
 * before anything is built.
 *
 */

/** The three values the INST gives `viewer_role`. A customer with no list, and a visitor, are `guest`. */
export type AnalyticsViewerRole = "internal" | "host" | "guest";

export type AnalyticsUser = {
  user_id?: string;
  anonymous_id?: string;
  first_touch_list_id?: string;
  logged_in: boolean;
  viewer_role: AnalyticsViewerRole;
};

/** What the block is made from: the kind of visitor, with the ids that decide the role. */
export type AnalyticsVisitor =
  | { kind: "visitor" }
  | { kind: "internal" }
  | { kind: "customer"; giftListId: string | null; analyticsUserId: string | null };

const optionalId = z.string().trim().min(1).optional().catch(undefined);

const visitorSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("customer"), giftListId: optionalId, analyticsUserId: optionalId }),
  z.object({ kind: z.literal("internal") }),
]);

/**
 * Reads the identity route's answer. Anything that is not exactly a customer or staff — a failed
 * request, another version of the answer — is a visitor with no session, never an error.
 */
export function parseAnalyticsVisitor(answer: unknown): AnalyticsVisitor {
  const parsed = visitorSchema.safeParse(answer);
  if (!parsed.success) return { kind: "visitor" };

  if (parsed.data.kind === "internal") return { kind: "internal" };

  return {
    kind: "customer",
    giftListId: parsed.data.giftListId ?? null,
    analyticsUserId: parsed.data.analyticsUserId ?? null,
  };
}

/** The panel's own territory: the host's routes, onboarding and list creation included. */
function isHostRoute(pathname: string): boolean {
  return pathname === "/anfitriao" || pathname.startsWith("/anfitriao/");
}

/**
 * `internal` for staff; `host` for a customer **with a list** who is on a host route or looking at
 * their own list; `guest` for everyone else — a customer with no list, a customer on someone
 * else's list, and a visitor with no session.
 *
 * `listId` is the `id` (UUID) of the list the page shows, or `null` on a page that shows none.
 */
export function resolveViewerRole(
  visitor: AnalyticsVisitor,
  where: { pathname: string; listId: string | null },
): AnalyticsViewerRole {
  if (visitor.kind === "internal") return "internal";
  if (visitor.kind === "visitor" || visitor.giftListId === null) return "guest";

  const isOwnList = where.listId !== null && where.listId === visitor.giftListId;

  return isHostRoute(where.pathname) || isOwnList ? "host" : "guest";
}

export function buildAnalyticsUser(
  visitor: AnalyticsVisitor,
  where: { pathname: string; listId: string | null },
  identity: IdentityCookies = {},
): AnalyticsUser {
  return {
    ...(visitor.kind === "customer" && visitor.analyticsUserId !== null ? { user_id: visitor.analyticsUserId } : {}),
    ...(identity.anonymousId ? { anonymous_id: identity.anonymousId } : {}),
    ...(identity.firstTouchListId ? { first_touch_list_id: identity.firstTouchListId } : {}),
    logged_in: visitor.kind !== "visitor",
    viewer_role: resolveViewerRole(visitor, where),
  };
}

/** Who is behind the browser, read from the identity route's answer (asked once per document). Never rejects. */
export async function loadAnalyticsVisitor(): Promise<AnalyticsVisitor> {
  return parseAnalyticsVisitor(await loadIdentityAnswer());
}

/**
 * The `user` block for a page, from the identity route's answer — which is asked once per document
 * and shared with Clarity (`loadIdentityAnswer`) — and from the identity cookies, which exist only
 * after the accept. Never rejects: any failure is a guest.
 */
export async function loadAnalyticsUser(where: { pathname: string; listId: string | null }): Promise<AnalyticsUser> {
  return buildAnalyticsUser(await loadAnalyticsVisitor(), where, readAnalyticsIdentity());
}
